import {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, TextField, Typography, Card } from '@mui/material';
import { Notes } from '@mui/icons-material';
import '../GoalData/dataEntry.css';
import { ACM } from '../../constants/UserRoles';
import AuthContext from '../../contexts/AuthContext';
import { addDoc, collection, doc, Timestamp } from 'firebase/firestore';
import { FirestoreDB } from '../../utils/firebase/firebase';
import { toast } from 'react-toastify';
import { editStudentQuickTrackEntry } from '../../api/students';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import OptionalBehaviorFields from './components/OptionalBehaviorFields';
import MeasurementFields from './components/MeasurementFields';
import Interventions from './components/Interventions';
import ActionButtons from './components/ActionButtons';
import BehaviorHeader from './components/BehaviorHeader';
import useBehaviorDataHandlers from './hooks/useBehaviorDataHandlers';
import { usePreRegisteredUsers } from '../../contexts/userDataContext';
import AddDataPointButton from '../GoalData/DataEntryModal/components/AddDataPointButton';


const normalizeQuestions = (questions) => {
  if (!questions) return {};
  if (Array.isArray(questions)) {
    return questions.reduce((acc, question) => {
      if (!question?.id) return acc;
      acc[question.id] =
        question.answer !== undefined
          ? question.answer
          : question.value !== undefined
          ? question.value
          : '';
      return acc;
    }, {});
  }
  return questions || {};
};

const hasNoCustomData = (customData) => {
  if (!customData) return true;
  if (Array.isArray(customData)) {
    return customData.length === 0;
  }
  return Object.keys(customData).length === 0;
};

const getBehaviorCustomSectionIds = (behaviorInfo) => {
  return (
    behaviorInfo?.DataEntrySelection?.CustomDataEntrySections ||
    behaviorInfo?.info?.DataEntrySelection?.CustomDataEntrySections ||
    behaviorInfo?.DataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    behaviorInfo?.info?.DataCollection?.DataEntrySelection
      ?.CustomDataEntrySections ||
    []
  );
};

// Helper function to convert customData structure to customSection structure
const convertCustomDataToCustomSection = (customData, behaviorInfo) => {
  if (hasNoCustomData(customData)) {
    return [];
  }

  // Get the custom section IDs from the behavior data
  const customSectionIds = getBehaviorCustomSectionIds(behaviorInfo);
  const shouldFilterByIds =
    Array.isArray(customSectionIds) && customSectionIds.length > 0;

  const hasSectionResponses = (questions) => {
    if (!questions) return false;
    if (Array.isArray(questions)) {
      return questions.some((question) => {
        const answer =
          question?.answer !== undefined
            ? question.answer
            : question?.value !== undefined
            ? question.value
            : '';
        if (Array.isArray(answer)) return answer.length > 0;
        if (answer && typeof answer === 'object') return true;
        return answer !== null && answer !== undefined && answer !== '';
      });
    }
    if (typeof questions === 'object') {
      return Object.values(questions).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') {
          if ('answer' in value && value.answer !== undefined) {
            const answer = value.answer;
            if (Array.isArray(answer)) return answer.length > 0;
            return answer !== null && answer !== '';
          }
          if ('value' in value && value.value !== undefined) {
            const v = value.value;
            if (Array.isArray(v)) return v.length > 0;
            return v !== null && v !== '';
          }
          return true;
        }
        return value !== null && value !== undefined && value !== '';
      });
    }
    return false;
  };

  const shouldKeepSection = (sectionId, questions) => {
    if (!sectionId) return false;
    if (!shouldFilterByIds) return true;
    if (customSectionIds.includes(sectionId)) return true;
    return hasSectionResponses(questions);
  };

  if (Array.isArray(customData)) {
    return customData
      .filter((section) => shouldKeepSection(section?.docId, section?.questions))
      .map((section) => ({
        docId: section.docId,
        title: section.title,
        definition: section.definition,
        questions: normalizeQuestions(section.questions),
      }));
  }

  // Check if this is the old flat format (keys contain underscores)
  const hasOldFormat = Object.keys(customData).some((key) => key.includes('_'));

  if (hasOldFormat) {
    // Handle old flat format: sectionId_questionId
    const sectionMap = new Map();

    Object.entries(customData).forEach(([key, value]) => {
      const [sectionId, questionId] = key.split('_');

      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          docId: sectionId,
          questions: {},
        });
      }

      sectionMap.get(sectionId).questions[questionId] = value;
    });

    return Array.from(sectionMap.values()).filter((section) =>
      shouldKeepSection(section.docId, section.questions)
    );
  }

  // Handle new nested object format
  const customSections = [];

  Object.entries(customData).forEach(([sectionId, questions]) => {
    // Only include sections that are part of this behavior
    if (shouldKeepSection(sectionId, questions)) {
      customSections.push({
        docId: sectionId,
        questions: normalizeQuestions(questions),
      });
    }
  });

  return customSections;
};

// Move these outside the component
function getPromptingStructure(type) {
  if (type === 'P+ & P-') {
    return { pMinus: '', pPlus: '' };
  } else if (type === 'IVP, DVP, PVP, GP, PP') {
    return { I: '', VP: '', IVP: '', DVP: '', PVP: '', GP: '', PP: '' };
  }
  return {};
}

function createInitialDataPoint(promptingType) {
  return {
    frequency: '',
    duration: '',
    severity: '',
    intensity: '',
    accuracy: '',
    independence: '',
    yesNo: '',
    selectedOptions: [],
    abcData: {
      antecedent: '',
      behavior: '',
      consequence: '',
      setting: '',
      notes: '',
    },
    settingLocation: '',
    phaseChange: '',
    eventLine: '',
    precedingBehavior: '',
    timeOfCollection: '',
    timeOfCollectionSpecific: '',
    notes: '',
    prompting: getPromptingStructure(promptingType),
    currentGoal: '',
    currentTime: new Date().toISOString(),
    interventions: [],
    // Staff present information
    additionalStaff: false,
    numberOfStaffs: 0,
    // Prompting level
    promptingLevel: '',
    // Custom data entry sections
    customSection: [],
    // New fields for session summary, group size, session length, and CPT codes
    sessionSummary: '',
    groupSize: '',
    sessionLength: '',
    cptCodes: '',
  };
}

const DataEntryModalForBehavior = forwardRef(({
  open = true,
  onClose,
  onSuccess,
  selectedDate,
  behavior,
  selectedDataPoint,
  videoId,
  fromSessionNotes,
  dataEntryRef,
  triggerSave = false,
  draftState = null,
  onDraftChange,
  onDraftClear,
  entryKey = null,
  registerModalData,
}, ref) => {
  const queryClient = useQueryClient();
  const { user, typeOfUser } = useContext(AuthContext);


  // eslint-disable-next-line no-unused-vars
  const [behaviorId, setBehaviorId] = useState(behavior?.id);
  // eslint-disable-next-line no-unused-vars
  const [studentId, setStudentId] = useState(behavior?.info?.StudentID);

  const [hasFrequency, setHasFrequency] = useState(false);
  const [hasDuration, setHasDuration] = useState(false);
  const [hasSeverity, setHasSeverity] = useState(false);
  const [hasIntensity, setHasIntensity] = useState(false);
  const [hasAccuracy, setHasAccuracy] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [promptingType, setPromptingType] = useState(
    behavior?.info?.DataCollection?.PromptingApplicable?.PromptingType
  );
  const [hasIndependent, setHasIndependent] = useState(false);
  const [hasYesNo, setHasYesNo] = useState(false);

  useEffect(() => {
    const types = behavior?.info?.DataCollection?.measurementType?.Type || [];

    const normalizedTypes = Array.isArray(types) ? types : [types];

    setHasFrequency(normalizedTypes.includes('Frequency'));
    setHasDuration(normalizedTypes.includes('Duration'));
    setHasSeverity(normalizedTypes.includes('Severity'));
    setHasIntensity(normalizedTypes.includes('Intensity'));
    setHasAccuracy(normalizedTypes.includes('% Accuracy'));
    setHasIndependent(normalizedTypes.includes('% Independent'));
    setHasYesNo(normalizedTypes.includes('Yes/No'));
  }, [behavior]);

  const [dataPoints, setDataPoints] = useState([
    createInitialDataPoint(promptingType),
  ]);
  const hasHydratedDraftRef = useRef(false);
  const draftChangeRef = useRef(onDraftChange);
  const lastEmittedDraftRef = useRef(null);
  useEffect(() => {
    if (selectedDataPoint && Array.isArray(selectedDataPoint.info)) {
      const prefillDataPoints = selectedDataPoint.info.map((infoItem) => {
        const selectedOptions = [];

        // Determine which options to add based on field values
        const abcData = infoItem.abc_data || {};
        if (
          (abcData.antecedent && abcData.antecedent.trim() !== '') ||
          (abcData.behavior && abcData.behavior.trim() !== '') ||
          (abcData.consequence && abcData.consequence.trim() !== '') ||
          (abcData.setting && abcData.setting.trim() !== '') ||
          (abcData.notes && abcData.notes.trim() !== '')
        ) {
          selectedOptions.push('Add ABC Data');
        }
        if (infoItem.event && infoItem.event.trim() !== '') {
          selectedOptions.push('Add Event Line');
        }
        if (
          infoItem.preceding_behavior &&
          infoItem.preceding_behavior.trim() !== ''
        ) {
          selectedOptions.push('Add Preceding Behavior');
        }
        if (
          infoItem.settingLocation &&
          infoItem.settingLocation.trim() !== ''
        ) {
          selectedOptions.push('Add Setting/Location');
        }
        if (infoItem.phase_change && infoItem.phase_change.trim() !== '') {
          selectedOptions.push('Add Phase Change Line');
        }
        if (
          infoItem.timeOfCollection &&
          infoItem.timeOfCollection.trim() !== ''
        ) {
          selectedOptions.push('Add Time of Collection');
        }

        // Check for staff present information
        if (infoItem.additionalStaff !== undefined) {
          selectedOptions.push('Add Staff Present Information');
        }

        // Check for prompting level
        if (infoItem.promptingLevel && infoItem.promptingLevel.trim() !== '') {
          selectedOptions.push('Add Prompting Level');
        }

        // Check for session summary
        if (infoItem.sessionSummary && infoItem.sessionSummary.trim() !== '') {
          selectedOptions.push('Add Session Summary');
        }

        // Check for group size
        if (infoItem.groupSize && infoItem.groupSize.trim() !== '') {
          selectedOptions.push('Add Group Size');
        }

        // Check for session length
        if (infoItem.sessionLength && infoItem.sessionLength !== '') {
          selectedOptions.push('Add Session Length');
        }

        // Check for CPT codes
        if (infoItem.cptCodes && infoItem.cptCodes.trim() !== '') {
          selectedOptions.push('Add CPT Code');
        }

        // Add custom section options based on customData content
        const convertedCustomSection = convertCustomDataToCustomSection(
          infoItem.customData,
          behavior?.info
        );
        convertedCustomSection.forEach((section) => {
          // Store the unique identifier rather than the label
          selectedOptions.push(section.docId);
        });

        return {
          id: infoItem.id || '',
          frequency: infoItem.frequency ?? 0,
          duration: infoItem.duration ?? 0,
          severity: infoItem.severity ?? 1,
          intensity: infoItem.intensity ?? 1,
          accuracy: infoItem.accuracy ?? 0,
          independence: infoItem.independence ?? 0,
          yesNo: infoItem.yesNo ?? '',
          selectedOptions,
          abcData: {
            antecedent: abcData.antecedent || '',
            behavior: abcData.behavior || '',
            consequence: abcData.consequence || '',
            setting: abcData.setting || '',
            notes: abcData.notes || '',
          },
          settingLocation: infoItem.settingLocation || '',
          phaseChange: infoItem.phase_change || '',
          eventLine: infoItem.event || '',
          precedingBehavior: infoItem.preceding_behavior || '',
          timeOfCollection: infoItem.timeOfCollection || '',
          timeOfCollectionSpecific: infoItem.timeOfCollectionSpecific || '',
          notes: infoItem.notes || '',
          prompting: infoItem.prompting || getPromptingStructure(promptingType),
          currentGoal: infoItem.currentGoal || '',
          currentTime: infoItem.currentTime || new Date().toISOString(),
          interventions: (() => {
            const interventionsData =
              infoItem.interventions || infoItem.intervention || [];

            if (typeof interventionsData === 'string') {
              try {
                return JSON.parse(interventionsData);
              } catch (error) {
                console.warn('Failed to parse interventions as JSON:', error);
                return [];
              }
            }

            if (Array.isArray(interventionsData)) {
              return interventionsData;
            }

            return [];
          })(),
          // Staff present information
          additionalStaff: infoItem.additionalStaff || false,
          numberOfStaffs: infoItem.numberOfStaffs || 0,
          // Prompting level
          promptingLevel: infoItem.promptingLevel || '',
          // Custom data entry sections
          customSection: convertCustomDataToCustomSection(
            infoItem.customData,
            behavior?.info
          ),
          // New fields for session summary, group size, session length, and CPT codes
          sessionSummary: infoItem.sessionSummary || '',
          groupSize: infoItem.groupSize || '',
          sessionLength: infoItem.sessionLength || '',
          cptCodes: infoItem.cptCodes || '',
        };
      });
      setDataPoints(prefillDataPoints);
    } else {
      setDataPoints([createInitialDataPoint(promptingType)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataPoint, promptingType]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: preRegisteredUsers = [] } = usePreRegisteredUsers(user?.uid);

  const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

  const handleConfirmDelete = () => {
    handleDelete(behavior?.id);
    handleCloseDeleteDialog();
  };

  const isPreRegistered = (id) => {
    return preRegisteredUsers.some((student) => student.id === id);
  };

  const showToastMessage = (status) => {
    if (fromSessionNotes) return;
    if (status === 'success') {
      toast.success('Data entry uploaded successfully!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else if (status === 'delete') {
      toast.success('Data entry deleted successfully!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else if (status === 'edit') {
      toast.success('Data entry updated successfully!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else {
      toast.error('Data entry upload failed!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const {
    handleDelete,
    handleCheckboxChange,
    handleTimeChange,
    handleNumericFieldChange,
    handleFieldChange,
    handleAbcChange,
    handlePromptingChange,
    handleSubmit,
    handleAddDataPoint,
  } = useBehaviorDataHandlers({
    setDataPoints,
    selectedDataPoint,
    onSuccess,
    onClose,
    studentId,
    ACM,
    typeOfUser,
    FirestoreDB,
    queryClient,
    user,
    dataPoints,
    Timestamp,
    dayjs,
    selectedDate,
    editStudentQuickTrackEntry,
    addDoc,
    collection,
    doc,
    videoId,
    showToastMessage,
    isPreRegistered,
    setIsDisabled,
    setIsDeleting,
    promptingType,
    fromSessionNotes,
    dataEntryRef,
    behaviorId,
  });

  useEffect(() => {
    draftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    if (!open) {
      hasHydratedDraftRef.current = false;
      return;
    }
    if (!draftState) return;
    if (hasHydratedDraftRef.current) return;
    
    // Don't hydrate draft if we have a selectedDataPoint (existing session data)
    // The selectedDataPoint prefill should take priority
    if (selectedDataPoint) {
      hasHydratedDraftRef.current = true;
      return;
    }

    const { dataPoints: draftDataPoints } = draftState;

    if (Array.isArray(draftDataPoints) && draftDataPoints.length > 0) {
      const clonedDataPoints = JSON.parse(JSON.stringify(draftDataPoints));
      setDataPoints(clonedDataPoints);
    }

    hasHydratedDraftRef.current = true;
  }, [draftState, open, setDataPoints, selectedDataPoint]);

  const serializedDraft = useMemo(() => {
    if (!open) return null;

    return {
      dataPoints: JSON.parse(JSON.stringify(dataPoints)),
    };
  }, [dataPoints, open]);

  useEffect(() => {
    if (!serializedDraft) return;
    if (typeof draftChangeRef.current !== 'function') return;

    const draftSignature = JSON.stringify(serializedDraft);
    if (lastEmittedDraftRef.current === draftSignature) {
      return;
    }

    lastEmittedDraftRef.current = draftSignature;
    draftChangeRef.current(serializedDraft);
  }, [serializedDraft]);

  useEffect(() => {
    if (typeof registerModalData !== 'function') return;
    if (!entryKey) return;

    registerModalData(entryKey, () => ({
      behaviorId: behaviorId || behavior?.id,
      behaviorName:
        behavior?.info?.BehaviorDetails?.Type?.BehaviorName ||
        behavior?.info?.BehaviorDetails?.Type?.SkillName ||
        behavior?.info?.BehaviorDetails?.Type?.Type ||
        '',
      dataPoints: JSON.parse(JSON.stringify(dataPoints)),
      selectedDate,
      selectedDataPoint: selectedDataPoint
        ? JSON.parse(JSON.stringify(selectedDataPoint))
        : null,
    }));
  }, [
    registerModalData,
    entryKey,
    behaviorId,
    behavior,
    dataPoints,
    selectedDate,
    selectedDataPoint,
  ]);

  const handleSubmitWithDraftClear = useCallback(
    async (behaviorIdToSubmit) => {
      const success = await handleSubmit(behaviorIdToSubmit);
      if (success && typeof onDraftClear === 'function') {
        onDraftClear();
      }
      return success;
    },
    [handleSubmit, onDraftClear]
  );

  // Expose save method via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      const resolvedBehaviorId =
        behaviorId || behavior?.id || entryKey?.split('-')?.[0] || null;

      // Call submit and capture the result
      const success = await handleSubmitWithDraftClear(resolvedBehaviorId);
      
      // Return the captured data entry ID or throw error if failed
      if (fromSessionNotes && dataEntryRef && resolvedBehaviorId) {
        const capturedId = dataEntryRef.current?.[resolvedBehaviorId];
        
        if (capturedId) {
          return { success: true, dataEntryId: capturedId, behaviorId: resolvedBehaviorId };
        } else if (success) {
          // Success but no ID captured - this is okay if it was an edit/update
          return { success: true, dataEntryId: null, behaviorId: resolvedBehaviorId };
        } else {
          throw new Error(`Failed to save quick track data for behavior ${resolvedBehaviorId}`);
        }
      }
      
      return { success, dataEntryId: null, behaviorId: resolvedBehaviorId };
    }
  }), [behaviorId, behavior, entryKey, fromSessionNotes, dataEntryRef, handleSubmitWithDraftClear]);

  return (
    <Card
      sx={{
        backgroundColor: '#e9eaff',
        borderRadius: '8px',
        padding: '0 10px',
      }}
    >
      <BehaviorHeader
        videoId={videoId}
        selectedDataPoint={selectedDataPoint}
        behavior={behavior}
      />
      <Box my={2} className='data-entry-model'>
        <Box alignItems='center'>
          {behavior?.info?.BehaviorDetails ? (
            <Typography fontWeight='bold' textAlign='center'>
              {behavior.info.BehaviorDetails.Type.Type}
              {behavior.info.BehaviorDetails.Type.Type === 'Behavior' &&
                behavior.info.BehaviorDetails.Type.BehaviorName &&
                `: ${behavior.info.BehaviorDetails.Type.BehaviorName}`}
              {behavior.info.BehaviorDetails.Type.Type ===
                'Skill Acquisition' &&
                behavior.info.BehaviorDetails.Type.SkillName &&
                `: ${behavior.info.BehaviorDetails.Type.SkillName}`}
            </Typography>
          ) : null}
          <Box />
        </Box>

        {dataPoints?.map((dataPoint, index) => (
          <Box
            key={index}
            sx={{ borderBottom: '1px solid gray', pb: 2, position: 'relative' }}
          >
            <MeasurementFields
              dataPoint={dataPoint}
              index={index}
              hasFrequency={hasFrequency}
              hasDuration={hasDuration}
              hasAccuracy={hasAccuracy}
              hasIndependent={hasIndependent}
              hasYesNo={hasYesNo}
              hasSeverity={hasSeverity}
              hasIntensity={hasIntensity}
              promptingType={promptingType}
              handleNumericFieldChange={handleNumericFieldChange}
              handleFieldChange={handleFieldChange}
              handlePromptingChange={handlePromptingChange}
            />
            <Interventions
              dataPoint={dataPoint}
              index={index}
              handleFieldChange={handleFieldChange}
              allGoalData={behavior?.info}
            />
            <OptionalBehaviorFields
              dataPoint={dataPoint}
              index={index}
              handleCheckboxChange={handleCheckboxChange}
              handleAbcChange={handleAbcChange}
              handleFieldChange={handleFieldChange}
              handleTimeChange={handleTimeChange}
              quicktrackInfo={behavior?.info}
              user={user}
            />
            <Box
              sx={{
                gap: 2,
                marginTop: '16px',
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f8f9ff',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  marginBottom: '12px',
                }}
              >
                <Notes sx={{ color: '#6b7280', fontSize: 18 }} />
                <Typography
                  variant='body1'
                  sx={{
                    color: '#1f2937',
                    fontWeight: 500,
                    fontSize: '1rem',
                  }}
                >
                  Notes:
                </Typography>
              </Box>
              <TextField
                fullWidth
                label='Add your notes here...'
                margin='dense'
                value={dataPoint.notes}
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6b7280',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9291ef',
                      },
                    },
                  },
                }}
                onChange={(e) =>
                  handleFieldChange(index, 'notes', e.target.value)
                }
              />
            </Box>
          </Box>
        ))}

        <AddDataPointButton
          MultipleDataPointsPerSession={(() => {
            const value =
              behavior?.info?.DataCollection?.MultipleDataPointsPerSession
                ?.MultipleDataPointsPerSession || 'No';

            return value;
          })()}
          dataPointPerSession={
            behavior?.info?.DataCollection?.MultipleDataPointsPerSession
              ?.MultipleDataPointsPerSession || 'No'
          }
          handleAddDataPoint={handleAddDataPoint}
        />

        <ActionButtons
          selectedDataPoint={selectedDataPoint}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          isDeleteDialogOpen={isDeleteDialogOpen}
          handleCloseDeleteDialog={handleCloseDeleteDialog}
          handleConfirmDelete={handleConfirmDelete}
          handleSubmit={handleSubmitWithDraftClear}
          behaviorId={behaviorId}
          isDisabled={isDisabled}
          isDeleting={isDeleting}
          fromSessionNotes={fromSessionNotes}
        />
      </Box>
    </Card>
  );
});

export default DataEntryModalForBehavior;
